import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';

// NMI card entry for checkout. The card number / expiry / CVV are NMI-hosted
// iframes (Collect.js "inline"), so card data never touches our pages; the
// payment token is posted as { dataDescriptor: 'COLLECTJS', dataValue: token }
// — the same pair Accept.js produces — and the API charges it on the store's
// NMI account. Authorize.net checkouts never load any of this.

export const isNmiGateway = (gateway) => gateway?.type === 'nmi';

// Can this checkout take a card at all? (Authorize.net needs its Accept.js
// keys, NMI its tokenization key.)
export const gatewayIsReady = (gateway) => gateway?.enabled === true && (isNmiGateway(gateway)
  ? !!gateway.tokenizationKey
  : !!(gateway.apiLoginId && gateway.publicClientKey));

let collectLoad = null;

// Collect.js reads its tokenization key from the script tag, so a page that
// needs a different key (another NMI account) swaps the script.
export const loadCollectJs = (gateway) => {
  const key = gateway.tokenizationKey;
  const host = (gateway.gatewayHost || 'secure.nmi.com').replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  if (collectLoad && collectLoad.key === key && collectLoad.host === host) return collectLoad.promise;
  document.querySelectorAll('script[data-arego-collectjs]').forEach((s) => s.remove());
  try { delete window.CollectJS; } catch { window.CollectJS = undefined; }
  const promise = new Promise((resolve, reject) => {
    const waitReady = (deadline) => {
      if (window.CollectJS?.configure) return resolve(window.CollectJS);
      if (Date.now() > deadline) return reject(new Error('The payment form did not finish loading — please refresh and try again.'));
      setTimeout(() => waitReady(deadline), 150);
    };
    const script = document.createElement('script');
    script.src = `https://${host}/token/Collect.js`;
    script.setAttribute('data-tokenization-key', key);
    script.setAttribute('data-variant', 'inline');
    script.setAttribute('data-arego-collectjs', '1');
    script.onload = () => waitReady(Date.now() + 6000);
    script.onerror = () => { collectLoad = null; reject(new Error('The payment form could not be loaded.')); };
    document.head.appendChild(script);
  });
  collectLoad = { key, host, promise };
  return promise;
};

const WALLET_TYPES = ['applePay', 'googlePay'];

// <NmiCardFields ref={nmiRef} gateway={skin.gateway} inputStyle={input} dark={t.dark}
//   total={quote?.total} wallets={!hasSubscription} onWalletToken={(token) => handlePay(token)} />
// nmiRef.current.tokenize() → Promise<{ dataDescriptor, dataValue }>.
//
// Wallet buttons (Apple Pay / Google Pay) render only when the NMI account has
// them switched on AND `wallets` is true: wallet tokens are single-use and
// can't be saved for subscription renewals, so subscription carts stay on
// card entry. A wallet sheet completes on its own, so its token arrives via
// onWalletToken rather than tokenize().
export const NmiCardFields = forwardRef(({ gateway, inputStyle, dark, total, wallets, onWalletToken, onLoadError }, ref) => {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const pending = useRef(null);
  const walletCb = useRef(onWalletToken);
  walletCb.current = onWalletToken;
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const applePay = wallets && gateway.applePayEnabled === true;
  const googlePay = wallets && gateway.googlePayEnabled === true;
  const walletsOn = applePay || googlePay;
  // Wallet sheets show the amount, so the price is part of the config; card
  // fields alone don't need re-rendering when the total moves.
  const price = walletsOn ? Number(total || 0).toFixed(2) : '';

  useImperativeHandle(ref, () => ({
    tokenize: () => new Promise((resolve, reject) => {
      if (!window.CollectJS || !ready) return reject(new Error('The payment form is still loading — please try again.'));
      pending.current = { resolve, reject };
      window.CollectJS.startPaymentRequest();
    }),
  }), [ready]);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    loadCollectJs(gateway).then((CollectJS) => {
      if (cancelled) return;
      const fields = {
        ccnumber: { selector: `#ccn-${uid}`, title: 'Card number', placeholder: 'Card number' },
        ccexp: { selector: `#cce-${uid}`, title: 'Expiry', placeholder: 'MM / YY' },
        cvv: { display: 'show', selector: `#cvv-${uid}`, title: 'CVC', placeholder: 'CVC' },
      };
      if (googlePay) fields.googlePay = { selector: `#gpay-${uid}`, buttonType: 'buy', buttonColor: dark ? 'white' : 'black' };
      if (applePay) fields.applePay = { selector: `#apay-${uid}`, type: 'buy', style: { 'button-style': dark ? 'white' : 'black', height: '44px', 'border-radius': '8px' } };
      CollectJS.configure({
        variant: 'inline',
        styleSniffer: false,
        // A selector that never exists: payment is started by tokenize(),
        // not by Collect.js hijacking a button click.
        paymentSelector: `#nmi-pay-${uid}-unused`,
        customCss: {
          color: dark ? '#ffffff' : '#1f2328',
          'background-color': 'transparent',
          border: 'none',
          'font-size': '14px',
          padding: '9px 11px',
          height: '38px',
        },
        invalidCss: { color: '#d63939' },
        placeholderCss: { color: dark ? 'rgba(255,255,255,.45)' : '#9aa0a6' },
        fields,
        ...(walletsOn ? { price, currency: 'USD', country: 'US' } : {}),
        timeoutDuration: 12000,
        timeoutCallback: () => {
          const p = pending.current;
          pending.current = null;
          p?.reject(new Error('Please check the card number, expiry and CVC.'));
        },
        fieldsAvailableCallback: () => { if (!cancelled) setReady(true); },
        callback: (response) => {
          const token = response?.token;
          if (WALLET_TYPES.includes(response?.tokenType)) {
            if (token) walletCb.current?.({ dataDescriptor: 'COLLECTJS', dataValue: token });
            return;
          }
          const p = pending.current;
          pending.current = null;
          if (!p) return;
          if (token) p.resolve({ dataDescriptor: 'COLLECTJS', dataValue: token });
          else p.reject(new Error('The card could not be tokenized.'));
        },
      });
    }).catch((e) => {
      if (cancelled) return;
      setLoadError(e.message);
      onLoadError?.(e.message);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateway.tokenizationKey, gateway.gatewayHost, uid, applePay, googlePay, price, dark]);

  const box = { ...inputStyle, padding: 0, height: 40, overflow: 'hidden' };
  return <div>
    {walletsOn && <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
      {applePay && <div id={`apay-${uid}`} />}
      {googlePay && <div id={`gpay-${uid}`} style={{ minHeight: 44 }} />}
      <div style={{ textAlign: 'center', fontSize: 12, opacity: 0.6 }}>or pay with card</div>
    </div>}
    <div id={`ccn-${uid}`} style={{ ...box, marginBottom: 8 }} />
    <div style={{ display: 'flex', gap: 8 }}>
      <div id={`cce-${uid}`} style={box} />
      <div id={`cvv-${uid}`} style={box} />
    </div>
    {!ready && !loadError && <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>Loading secure card form…</div>}
    {loadError && <div style={{ fontSize: 12, color: '#d63939', marginTop: 6 }}>{loadError}</div>}
  </div>;
});
NmiCardFields.displayName = 'NmiCardFields';
